import type { Answers } from "@/schema/quizSchema";

export type PresentationBand = "ISOLATED" | "CLUSTERED" | "BROAD";
export type ExposureBand = "ACTIVE" | "SUSTAINED" | "SATURATED";
export interface WindowMath {
  used: number;
  left: number;
  total: number;
  closed: boolean;
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const list = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

/** Points for one answer, from a value -> points table. Unknown values score 0. */
const points = (value: string, table: Record<string, number>): number => table[value] ?? 0;

/**
 * Presentation: what the parent OBSERVES. 0-9.
 *
 * Deliberately describes her report, never his biology. Nothing here licenses a
 * causal claim; the result page says so out loud. See the spec's claim constraint.
 */
export function presentationScore(answers: Answers): number {
  return (
    points(str(answers.energy), { done: 2, flat: 2, resurfaces: 1 }) +
    points(str(answers.build), { behind: 2, abit: 1 }) +
    points(str(answers.chest), { bothers: 2, babyfat: 2, maybe: 1 }) +
    points(str(answers.drive), { quiet: 2, both: 2, shortfuse: 1, some: 1 }) +
    points(str(answers.odor), { hour: 1, never: 1 })
  );
}

export function presentationBand(score: number): PresentationBand {
  if (score >= 6) return "BROAD";
  if (score >= 3) return "CLUSTERED";
  return "ISOLATED";
}

/**
 * Exposure: what is ON him daily. 0-5. A product-audit fact, not a health claim.
 *
 * A shower-frequency axis was deliberately cut: "when I make him" LOWERED the
 * score, so a parent reporting that her son barely showers got a milder verdict.
 * The athlete cohort is still identified by the odor question.
 */
export function exposureScore(answers: Answers): number {
  // "justwash" is the exclusive option: it means no products beyond the wash.
  const extras = list(answers.products).filter((v) => v !== "justwash").length;
  const productPts = extras >= 4 ? 2 : extras >= 2 ? 1 : 0;

  const durationPts = points(str(answers.duration), { "1to2": 1, "3to4": 2, since: 2 });
  const heatPts = str(answers.heat) === "steam" ? 1 : 0;

  return productPts + durationPts + heatPts;
}

export function exposureBand(score: number): ExposureBand {
  if (score >= 4) return "SATURATED";
  if (score >= 2) return "SUSTAINED";
  return "ACTIVE";
}

/**
 * The testosterone-driven build window, taken as ages 11 to 16.
 *
 * NOTE: the bounds are a design choice, not a cited figure, and "left" is the
 * most prominent number on the result page. Pending client sign-off — see the
 * spec's open questions before changing them.
 */
const WINDOW_TOTAL = 1825;

const DAYS_USED: Record<string, number> = {
  // Never zero: development opens before nine (adrenarche), so by the time a
  // parent is taking this quiz some of the window is already spent. A zero
  // here also rendered as "nothing has happened yet", the opposite of the
  // urgency the number exists to carry (client feedback 2026-08-20).
  "9-10": 183,
  "11-12": 365,
  "13-14": 1095,
  "15-16": 1642,
  "17plus": WINDOW_TOTAL,
};

export function windowMath(age: string): WindowMath {
  const raw = DAYS_USED[age] ?? 0;
  const used = Math.min(Math.max(raw, 0), WINDOW_TOTAL);
  return {
    used,
    left: WINDOW_TOTAL - used,
    total: WINDOW_TOTAL,
    closed: used >= WINDOW_TOTAL,
  };
}
